import React from 'react';
import * as Sentry from '@sentry/react';
import ErrorState from './ErrorState';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Forward to Sentry so production crashes surface in the dashboard with a
    // React component stack — without this, the fallback UI silently swallows
    // every render exception (cost us a day chasing a TDZ regression in
    // GroupDetail that should have been visible immediately).
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo?.componentStack } },
    });

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught:', error, errorInfo);
    }

    this.setState({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null 
    });
    
    // If a reset callback is provided, call it
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom error UI can be passed as children or use default
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      // Default error UI using ErrorState component
      return (
        <ErrorState
          title={this.props.title || "Something went wrong"}
          message={
            this.props.message || 
            (this.state.error?.message || "An unexpected error occurred. Please try again.")
          }
          showRetry={this.props.showRetry !== false}
          onRetry={this.handleReset}
          showHome={this.props.showHome !== false}
          actionLabel={this.props.actionLabel}
          onAction={this.props.onAction}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

